import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingCart, ArrowRight, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/context/CartContext";
import { getBrand, formatPrice, displayPrice, resolveImage } from "@/data/catalog";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal, hasRequestPrice, count } = useCart();

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Seo title="Your Cart | CA HVAC Direct" description="Review the HVAC equipment in your cart." />
      <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Your Cart</h1>

      {items.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="cart-empty">
          <ShoppingCart className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-4 font-heading text-lg font-bold text-slate-900">Your cart is empty</p>
          <p className="mt-1 text-sm text-slate-500">Browse the catalog to add HVAC equipment.</p>
          <Button asChild className="mt-6 rounded-sm bg-sky-700 hover:bg-sky-800" data-testid="cart-continue-shopping"><Link to="/shop">Continue Shopping</Link></Button>
        </div>
      ) : (
        <div className="mt-8 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
              {items.map((item) => {
                const dp = displayPrice(item);
                const img = resolveImage(item.image);
                return (
                  <div key={item.id} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center" data-testid={`cart-item-${item.slug}`}>
                    <div className="flex min-w-0 flex-1 items-center gap-4">
                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-sm border border-slate-200 bg-slate-100">
                        {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : <img src="/logo.png" alt="" className="h-full w-full object-contain opacity-30" />}
                      </div>
                      <div className="min-w-0">
                        <span className="text-[11px] font-semibold uppercase tracking-widest text-sky-700">{getBrand(item.brandSlug)?.name}</span>
                        <Link to={`/product/${item.slug}`} className="block truncate font-heading font-bold text-slate-900 hover:text-sky-700">{item.name}</Link>
                        <span className="font-mono text-xs text-slate-500">Model: {item.model}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-4 sm:justify-end">
                      <div className="inline-flex items-center rounded-sm border border-slate-300">
                        <button className="flex h-8 w-8 items-center justify-center text-slate-600 hover:bg-slate-50" onClick={() => updateQty(item.id, item.qty - 1)} aria-label="Decrease" data-testid={`cart-decrease-${item.slug}`}><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-9 text-center text-sm font-semibold" data-testid={`cart-qty-${item.slug}`}>{item.qty}</span>
                        <button className="flex h-8 w-8 items-center justify-center text-slate-600 hover:bg-slate-50" onClick={() => updateQty(item.id, item.qty + 1)} aria-label="Increase" data-testid={`cart-increase-${item.slug}`}><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="w-24 text-right font-heading font-bold text-slate-900">
                        {dp.mode === "show" ? formatPrice(dp.price * item.qty) : <span className="text-xs font-medium text-slate-500">Request Price</span>}
                      </div>
                      <button className="text-slate-400 hover:text-red-600" onClick={() => removeItem(item.id)} aria-label="Remove item" data-testid={`cart-remove-${item.slug}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button asChild variant="ghost" className="mt-4 rounded-sm text-slate-600"><Link to="/shop"><ArrowLeft className="h-4 w-4" /> Continue Shopping</Link></Button>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-28 rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Order Summary</h2>
              <Separator className="my-4" />
              <div className="flex justify-between text-sm text-slate-600"><span>Items</span><span data-testid="summary-item-count">{count}</span></div>
              <div className="mt-2 flex justify-between text-sm text-slate-600"><span>Priced subtotal</span><span className="font-semibold text-slate-900" data-testid="summary-subtotal">{subtotal > 0 ? formatPrice(subtotal) : "—"}</span></div>
              {hasRequestPrice && <p className="mt-3 text-xs text-amber-600">Some items are Request Price. You can submit a quote request at checkout — nothing is charged for those items.</p>}
              <Button asChild className="mt-6 w-full rounded-sm bg-sky-700 hover:bg-sky-800" data-testid="cart-checkout-button"><Link to="/checkout">{hasRequestPrice ? "Request Quote / Checkout" : "Proceed to Checkout"} <ArrowRight className="h-4 w-4" /></Link></Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
